import type { FC, FormEvent, ReactNode } from 'react'

import { useState } from 'react'
import { toast } from 'sonner'
import { CalendarClock } from 'lucide-react'
import { useClinic } from '@/hooks/use-clinic'
import { formatDate } from '@/lib/clinic/datetime'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import Field from '@/components/common/field'
import { QuickDatePicker } from '@/components/follow-ups/follow-up-fields'

import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogTitle
} from '@/components/ui/dialog'

interface RescheduleDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    followUpId: string | null
}

interface RescheduleBodyProps {
    followUpId: string | null
    onClose: () => void
}

const RescheduleBody: FC<RescheduleBodyProps> = ({ followUpId, onClose }): ReactNode => {
    const { index, actions, today } = useClinic()
    const followUp = followUpId ? index.followUpsById.get(followUpId) ?? null : null
    const patient = followUp ? index.patientsById.get(followUp.patientId) ?? null : null

    const [dueDate, setDueDate] = useState<string | null>(null)
    const [reason, setReason] = useState('')
    const [error, setError] = useState<string | null>(null)

    if (!followUp || !patient) {
        return (
            <div className='p-6'>
                <DialogTitle>Follow-up not found</DialogTitle>

                <DialogDescription className='mt-2'>
                    This follow-up is no longer available.
                </DialogDescription>
            </div>
        )
    }

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        if (!dueDate) {
            setError('Choose the new returning date.')
            return
        }

        if (dueDate === followUp.dueDate) {
            setError('Pick a date different from the current one.')
            return
        }

        actions.rescheduleFollowUp(followUp.id, dueDate, reason)
        toast.success(`Rescheduled to ${formatDate(dueDate)}`, { description: patient.name })
        onClose()
    }

    return (
        <>
            <div className='flex items-start gap-3 border-b px-5 py-4 pr-12'>
                <span className='flex size-10 shrink-0 items-center justify-center rounded-xl bg-accent text-accent-foreground'>
                    <CalendarClock className='size-5' />
                </span>

                <div className='flex min-w-0 flex-col gap-1'>
                    <DialogTitle className='text-lg font-semibold'>
                        Reschedule follow-up
                    </DialogTitle>

                    <DialogDescription>
                        {patient.name} · currently due {formatDate(followUp.dueDate)}
                    </DialogDescription>
                </div>
            </div>

            <form id='reschedule-form' noValidate onSubmit={handleSubmit} className='flex flex-col gap-5 overflow-y-auto px-5 py-5'>
                <Field label='New returning date' htmlFor='reschedule-date' required error={error}>
                    <QuickDatePicker
                        id='reschedule-date'
                        value={dueDate}
                        onChange={value => {
                            setDueDate(value)
                            setError(null)
                        }}
                        today={today}
                        invalid={Boolean(error)}
                    />
                </Field>

                <Field label='Reason' htmlFor='reschedule-reason' hint='Optional. Saved in the change history.'>
                    <Input
                        id='reschedule-reason'
                        value={reason}
                        onChange={event => setReason(event.target.value)}
                        placeholder='e.g. Patient travelling, asked for next week'
                    />
                </Field>
            </form>

            <div className='flex flex-col-reverse gap-2 border-t bg-muted/40 px-5 py-3.5 sm:flex-row sm:justify-end'>
                <Button type='button' variant='ghost' onClick={onClose}>
                    Cancel
                </Button>

                <Button type='submit' form='reschedule-form' size='lg'>
                    Reschedule
                </Button>
            </div>
        </>
    )
}

const RescheduleDialog: FC<RescheduleDialogProps> = ({ open, onOpenChange, followUpId }): ReactNode => {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className='flex max-h-[calc(100dvh-2rem)] flex-col gap-0 overflow-hidden p-0 sm:max-w-md'>
                <RescheduleBody followUpId={followUpId} onClose={() => onOpenChange(false)} />
            </DialogContent>
        </Dialog>
    )
}

export default RescheduleDialog
